import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { QuizRunner, QuizResult } from "@/components/QuizRunner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, ShieldCheck, ClipboardList, Wrench, MessageSquare, PenLine, Award } from "lucide-react";

function Section({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-white border border-steel rounded-md p-5 sm:p-6" data-testid={testid}>
      <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-base mb-4 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-lomond" />} {title}
      </h2>
      {children}
    </section>
  );
}

export default function TrainingDay() {
  const { day } = useParams();
  const dayNum = parseInt(day, 10);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reflection, setReflection] = useState({ learned: "", difficult: "", help: "" });
  const [savingReflection, setSavingReflection] = useState(false);
  const [ftQuestions, setFtQuestions] = useState(null);
  const [ftResult, setFtResult] = useState(null);

  const load = () => api.get(`/training/days/${dayNum}`)
    .then((r) => {
      setData(r.data);
      setQuizResult(null);
      if (r.data.day.reflection) setReflection(r.data.day.reflection);
    })
    .catch((e) => { toast.error(apiError(e)); navigate("/training"); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dayNum]);

  // Resume an in-progress foundation theory attempt
  useEffect(() => {
    if (dayNum === 10 && data?.final_theory?.in_progress && !ftQuestions && !ftResult) {
      api.get("/training/final-theory").then((r) => {
        if (r.data.attempt) setFtQuestions(r.data.attempt.questions);
      }).catch(() => {});
    }
  }, [data, dayNum, ftQuestions, ftResult]);

  if (!data) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="day-loading" />;
  const { module: m, day: rec, quiz_questions, quiz_history, final_theory } = data;

  const markTheory = async () => {
    try {
      await api.post(`/training/days/${dayNum}/theory-complete`);
      toast.success("Theory marked complete.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitQuiz = async (answers) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/training/days/${dayNum}/quiz/submit`, { answers });
      setQuizResult(r.data);
      return r.data;
    } catch (e) { toast.error(apiError(e)); return null; } finally { setSubmitting(false); }
  };

  const startFinalTheory = async () => {
    try {
      await api.post("/training/final-theory/start");
      const r = await api.get("/training/final-theory");
      setFtQuestions(r.data.attempt?.questions || null);
      setFtResult(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitFinalTheory = async (answers) => {
    setSubmitting(true);
    try {
      const r = await api.post("/training/final-theory/submit", { answers });
      setFtResult(r.data);
      return r.data;
    } catch (e) { toast.error(apiError(e)); return null; } finally { setSubmitting(false); }
  };

  const saveReflection = async () => {
    setSavingReflection(true);
    try {
      await api.post(`/training/days/${dayNum}/reflection`, reflection);
      toast.success("Reflection saved — your instructor can see this.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSavingReflection(false); }
  };

  const lastAttempt = quiz_history?.length ? quiz_history[quiz_history.length - 1] : null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="training-day-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/training")} className="border-steel text-charcoal bg-white" data-testid="back-to-camp">
          <ArrowLeft className="h-4 w-4 mr-1" /> Training Camp
        </Button>
        {rec.completed && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-emerald-100 border-emerald-300 text-emerald-800 text-xs font-mono uppercase tracking-widest" data-testid="day-complete-chip">
            <CheckCircle2 className="h-3.5 w-3.5" /> Day complete — signed off
          </span>
        )}
      </div>

      <section className="bg-charcoal text-white rounded-md p-6" data-testid="day-header">
        <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-lomond">Day {dayNum} of 10</p>
        <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl mt-1" data-testid="day-title">{m.title}</h1>
        <div className="flex gap-2 mt-4 flex-wrap">
          {[
            ["Theory", rec.theory_completed],
            [dayNum === 10 ? "Foundation theory" : `Quiz ${m.pass_mark}%+`, dayNum === 10 ? final_theory?.status === "pass" : rec.quiz_passed],
            ["Practical", rec.practical_recorded],
            ["Reflection", !!rec.reflection],
            ["Sign-off", !!rec.signoff],
          ].map(([label, done]) => (
            <span key={label} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
              done ? "bg-emerald-400/20 border-emerald-400/40 text-emerald-300" : "bg-white/5 border-white/15 text-white/50"
            }`} data-testid={`chip-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {label}
            </span>
          ))}
        </div>
      </section>

      <Section title="1. Today's Objectives" icon={ClipboardList} testid="objectives-section">
        <ul className="space-y-2">
          {m.objectives.map((o, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-slate-700">
              <span className="font-mono text-xs text-lomond font-bold shrink-0 mt-0.5">{i + 1}.</span>{o}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="2. Theory Learning" icon={ShieldCheck} testid="theory-section">
        <div className="space-y-4">
          {m.theory.map((p, i) => <p key={i} className="text-sm text-slate-700 leading-relaxed">{p}</p>)}
        </div>
        {m.images?.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3 mt-5" data-testid="theory-images">
            {m.images.map((url) => <img key={url} src={fileUrl(url)} alt="Training" className="rounded-md border border-steel object-cover w-full" />)}
          </div>
        )}
        {m.video_url && (
          <div className="mt-4" data-testid="theory-video">
            {/\.(mp4|webm)$/i.test(m.video_url)
              ? <video controls src={fileUrl(m.video_url)} className="w-full rounded-md border border-steel" />
              : <a href={m.video_url} target="_blank" rel="noreferrer" className="text-sm text-lomond underline font-semibold">Watch the training video</a>}
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-steel">
          {rec.theory_completed ? (
            <p className="text-sm text-emerald-700 font-semibold flex items-center gap-2" data-testid="theory-done">
              <CheckCircle2 className="h-4 w-4" /> Theory completed
            </p>
          ) : (
            <Button onClick={markTheory} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="theory-complete-btn">
              I've read the theory — mark complete
            </Button>
          )}
        </div>
      </Section>

      <Section title="3. Key Safety Messages" icon={ShieldCheck} testid="safety-section">
        <div className="space-y-2.5">
          {m.safety_messages.map((s, i) => (
            <div key={i} className="bg-charcoal text-white rounded-sm px-4 py-3 flex gap-3 items-start">
              <div className="hazard-stripes w-1.5 self-stretch rounded-sm shrink-0" />
              <p className="text-sm font-semibold leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </Section>

      {dayNum !== 10 ? (
        <Section title={`4. Daily Quiz — pass mark ${m.pass_mark}%`} icon={ClipboardList} testid="quiz-section">
          {rec.quiz_passed && !quizResult ? (
            <div className="space-y-4">
              <p className="bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-md px-4 py-3 text-sm font-semibold" data-testid="quiz-passed-note">
                Quiz passed — best score {rec.quiz_best_pct}% ({rec.quiz_attempts} attempt{rec.quiz_attempts > 1 ? "s" : ""})
              </p>
              {lastAttempt?.responses?.length > 0 && (
                <QuizResult result={{ score_pct: lastAttempt.pct, passed: true, pass_mark: m.pass_mark, sc_wrong: lastAttempt.sc_wrong, responses: lastAttempt.responses }} testidPrefix="quiz" />
              )}
            </div>
          ) : quizResult ? (
            <QuizResult result={quizResult} testidPrefix="quiz" onRetry={() => { setQuizResult(null); load(); }} />
          ) : quiz_questions ? (
            <>
              {quiz_history?.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4" data-testid="quiz-retry-note">
                  Last attempt {lastAttempt.pct}% (need {m.pass_mark}%). Re-read the theory above, then try again.
                </p>
              )}
              <QuizRunner questions={quiz_questions} passMark={m.pass_mark} onSubmit={submitQuiz} submitting={submitting} testidPrefix="quiz" />
            </>
          ) : (
            <p className="text-sm text-slate-500">Quiz loading...</p>
          )}
        </Section>
      ) : (
        <Section title="4. Lomond Foundation Theory Assessment" icon={Award} testid="final-theory-section">
          {final_theory?.status && !ftResult && !ftQuestions ? (
            <div className="space-y-3" data-testid="ft-status">
              <p className={`rounded-md border px-4 py-3 text-sm font-semibold ${
                final_theory.status === "pass" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                : final_theory.status === "management_review" ? "bg-amber-50 border-amber-300 text-amber-800"
                : "bg-red-50 border-red-300 text-red-700"
              }`}>
                Latest result: {final_theory.score_pct}% — {final_theory.status === "pass" ? "PASSED" : final_theory.status === "management_review" ? "Management review required" : "Not passed"} ({final_theory.attempts} attempt{final_theory.attempts > 1 ? "s" : ""})
              </p>
              {final_theory.status !== "pass" && (
                <Button onClick={startFinalTheory} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="ft-retake-btn">Revisit learning &amp; retake assessment</Button>
              )}
            </div>
          ) : ftResult ? (
            <QuizResult result={ftResult} testidPrefix="ft" onRetry={() => { setFtResult(null); setFtQuestions(null); load(); }} />
          ) : ftQuestions ? (
            <QuizRunner questions={ftQuestions} passMark={80} onSubmit={submitFinalTheory} submitting={submitting} testidPrefix="ft" />
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                50-60 questions drawn from everything covered on the camp. Pass mark set by Lomond. Safety-critical questions
                carry special weight — a high score cannot cancel out unsafe judgement.
              </p>
              <Button onClick={startFinalTheory} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="ft-start-btn">Start Foundation Theory Assessment</Button>
            </div>
          )}
        </Section>
      )}

      <Section title="5. Practical Training Tasks" icon={Wrench} testid="practical-section">
        <ul className="space-y-2.5">
          {m.practical_exercises.map((p, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700" data-testid={`practical-task-${i}`}>
              {rec.practical_tasks?.[String(i)] || rec.practical_tasks?.[i]
                ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                : <Circle className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" />}
              {p}
            </li>
          ))}
        </ul>
        {rec.practical_recorded ? (
          <p className="text-xs text-emerald-700 font-semibold mt-3" data-testid="practical-recorded-note">
            Practical recorded by {rec.practical_by_name || "your instructor"}{rec.practical_notes ? ` — ${rec.practical_notes}` : ""}
          </p>
        ) : (
          <p className="text-xs text-slate-500 mt-3">Your instructor records the practical assessment beside the machine.</p>
        )}
      </Section>

      <Section title="6. Instructor Assessment & Comments" icon={MessageSquare} testid="instructor-section">
        {rec.instructor_comments ? (
          <p className="text-sm text-slate-700 border-l-2 border-lomond pl-3 leading-relaxed" data-testid="instructor-comments">{rec.instructor_comments}</p>
        ) : (
          <p className="text-sm text-slate-500">No instructor comments yet today.</p>
        )}
        <div className="mt-4 pt-4 border-t border-steel">
          {rec.signoff ? (
            <p className="text-sm text-emerald-700 font-semibold flex items-center gap-2" data-testid="signoff-done">
              <CheckCircle2 className="h-4 w-4" /> Signed off by {rec.signoff.by_name} — {new Date(rec.signoff.at).toLocaleString("en-GB")}
            </p>
          ) : (
            <p className="text-sm text-slate-500" data-testid="signoff-pending">Awaiting instructor sign-off.</p>
          )}
        </div>
      </Section>

      <Section title="7. Your Daily Reflection" icon={PenLine} testid="reflection-section">
        {rec.reflection && !savingReflection ? (
          <div className="space-y-3" data-testid="reflection-saved">
            {[["What did you learn today?", rec.reflection.learned], ["What did you find most difficult?", rec.reflection.difficult], ["Anything you'd like more help with?", rec.reflection.help]].map(([ql, a]) => (
              <div key={ql}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{ql}</p>
                <p className="text-sm text-charcoal mt-0.5">{a}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[["learned", "What did you learn today?"], ["difficult", "What did you find most difficult?"], ["help", "Is there anything you would like more help with?"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <label className="text-sm text-slate-600">{label}</label>
                <Textarea value={reflection[k]} onChange={(e) => setReflection({ ...reflection, [k]: e.target.value })}
                  className="bg-white border-steel text-charcoal min-h-16" data-testid={`reflection-${k}`} />
              </div>
            ))}
            <Button onClick={saveReflection} disabled={savingReflection} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="reflection-save-btn">
              {savingReflection ? "Saving..." : "Save Reflection"}
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
}
